import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import Head from "../../../../layout/head/Head";
import Content from "../../../../layout/content/Content";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  Icon,
  PaginationComponent,
} from "../../../../components/Component";
import { useDispatch, useSelector } from "react-redux";
import { Badge, DropdownItem, DropdownMenu, DropdownToggle, Modal, ModalBody, ModalHeader, UncontrolledDropdown } from "reactstrap";
import * as app_consts from "../../../../constants/react_app_consts";
import { employee_dashboard_actions_helper } from "../../../../actions/actions_helper";
import {
  fail_retrieve_audit_trails,
  loading_retrieve_audit_trails,
  reset_retrieve_audit_trails,
  success_retrieve_audit_trails,
} from "../../../../reducers/dashboard_reducers/employee/retrieve_audit_trails_Slice";
import {
  RETRIEVE_AUDIT_TRAILS_ACCESS_DENIED_MSG,
  RETRIEVE_AUDIT_TRAILS_ERR_MSG,
  RETRIEVE_AUDIT_TRAILS_SUCCESS_MSG,
} from "../../../../constants/success_error_messages_consts";
import {
  RETRIEVE_AUDIT_TRAILS_BY_DATE_RANGE_API,
  RETRIEVE_CURRENT_MONTH_AUDIT_TRAILS_API,
} from "../../../../api/api_data";
import { formatToSentenceCase } from "../../../../utils/functionalUtils";
import { messageToast } from "../../../../utils/Utils";
import AuthError from "../../../components/page_messaging/AuthError";
import AuthLoader from "../../../components/page_messaging/AuthLoader";
import AuthNoData from "../../../components/page_messaging/AuthNoData";
import LabelText from "../../components/text/LabelText";
import usePersistentState from "../../../../Hooks/usePersistentState";

const getAuditTrail = (entry) => entry?.audit_trail || entry?.audit || {};
const getAuditUser = (entry) => entry?.user || {};

const normalizeAuditTrailRecord = (entry) => {
  const auditTrail = getAuditTrail(entry);
  const user = getAuditUser(entry);

  return {
    ...entry,
    user: {
      ...user,
      last_name: user?.last_name || user?.surname || "",
    },
    audit: auditTrail,
    audit_trail: auditTrail,
  };
};

const getAuditStatusColor = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();

  if (normalizedStatus === "success") return "success";
  if (normalizedStatus === "retrieve" || normalizedStatus === "request") return "warning";
  return "danger";
};

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value) && !(value instanceof Date);

const formatAuditLabel = (value) => formatToSentenceCase(String(value || "").replace(/_/g, " "));

const formatAuditValue = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    console.error("Failed to format audit value.", error);
    return String(value);
  }
};

const getAuditUserName = (entry) => {
  const user = getAuditUser(entry);
  return [user?.first_name, user?.last_name || user?.surname].filter(Boolean).join(" ").trim() || "--";
};

const sortAuditTrails = (auditTrails = []) =>
  [...auditTrails].sort((a, b) => {
    const left = getAuditTrail(a)?.inserted_at ? new Date(getAuditTrail(a).inserted_at).getTime() : 0;
    const right = getAuditTrail(b)?.inserted_at ? new Date(getAuditTrail(b).inserted_at).getTime() : 0;
    return right - left;
  });

const shouldIgnoreRowClick = (target) => {
  if (!target || typeof target.closest !== "function") return false;

  return Boolean(
    target.closest("a, button, input, select, textarea, label, .nk-tb-col-tools, .dropdown-toggle, .dropdown-menu")
  );
};

const renderAuditDataRows = (value, parentKey = "root") => {
  if (!isPlainObject(value)) {
    return (
      <div className="col-12" key={parentKey}>
        <pre className="caption-text text-break bg-light rounded p-3 mt-1 mb-0">
          {formatAuditValue(value)}
        </pre>
      </div>
    );
  }

  return Object.entries(value).map(([key, entryValue]) => {
    const composedKey = `${parentKey}.${key}`;
    const normalizedKey = String(key || "").toLowerCase();

    if (normalizedKey === "params") {
      return null;
    }

    if (isPlainObject(entryValue)) {
      return (
        <div className="col-12" key={composedKey}>
          <div className="card card-bordered">
            <div className="card-inner">
              <div className="mb-3">
                <span className="sub-text">{formatAuditLabel(key)}</span>
              </div>
              <div className="row g-4">
                {renderAuditDataRows(entryValue, composedKey)}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (Array.isArray(entryValue)) {
      return (
        <div className="col-12" key={composedKey}>
          <span className="sub-text">{formatAuditLabel(key)}</span>
          <pre className="caption-text text-break bg-light rounded p-3 mt-1 mb-0">
            {formatAuditValue(entryValue)}
          </pre>
        </div>
      );
    }

    const formattedValue = formatAuditValue(entryValue);
    const isMultiline = formattedValue.includes("\n");

    if (isMultiline) {
      return (
        <div className="col-12" key={composedKey}>
          <span className="sub-text">{formatAuditLabel(key)}</span>
          <pre className="caption-text text-break bg-light rounded p-3 mt-1 mb-0">
            {formattedValue}
          </pre>
        </div>
      );
    }

    return (
      <LabelText
        key={composedKey}
        text={formattedValue}
        label={formatAuditLabel(key)}
      />
    );
  });
};

const AuditTrailsList = () => {
  const dispatch = useDispatch();

  const [page_data, setPageData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [auditTrailsModal, setAuditTrailsModal] = useState(false);
  const [sm, updateSm] = useState(false);
  const [searchTerm, setSearchTerm] = usePersistentState("audit_trails_list:searchTerm", "");
  const [selectedStatusFilter, setSelectedStatusFilter] = usePersistentState(
    "audit_trails_list:selectedStatusFilter",
    "All Statuses"
  );
  const [selectedTypeFilter, setSelectedTypeFilter] = usePersistentState(
    "audit_trails_list:selectedTypeFilter",
    "All Types"
  );
  const [startDate, setStartDate] = usePersistentState("audit_trails_list:startDate", "");
  const [endDate, setEndDate] = usePersistentState("audit_trails_list:endDate", "");
  const [currentPage, setCurrentPage] = usePersistentState("audit_trails_list:currentPage", 1);
  const [itemPerPage] = useState(10);
  const [activeRangeLabel, setActiveRangeLabel] = useState("Current Month");
  const skipInitialPageResetRef = useRef(true);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const retrieveAuditTrails = (endpoint, nextRangeLabel) => {
    setActiveRangeLabel(nextRangeLabel);
    dispatch(
      employee_dashboard_actions_helper(
        loading_retrieve_audit_trails,
        success_retrieve_audit_trails,
        fail_retrieve_audit_trails,
        RETRIEVE_AUDIT_TRAILS_ERR_MSG,
        RETRIEVE_AUDIT_TRAILS_SUCCESS_MSG,
        app_consts.GET,
        endpoint,
        null,
        RETRIEVE_AUDIT_TRAILS_ACCESS_DENIED_MSG
      )
    );
  };

  const retrieveCurrentMonthAuditTrails = () => {
    retrieveAuditTrails(RETRIEVE_CURRENT_MONTH_AUDIT_TRAILS_API, "Current Month");
  };

  const retrieveAuditTrailsByDateRange = () => {
    if (!startDate || !endDate) {
      messageToast("Select both a start date and end date.", app_consts.FAIL);
      return;
    }

    if (moment(startDate).isAfter(moment(endDate), "day")) {
      messageToast("Start date cannot be after end date.", app_consts.FAIL);
      return;
    }

    retrieveAuditTrails(
      RETRIEVE_AUDIT_TRAILS_BY_DATE_RANGE_API(startDate, endDate),
      `${startDate} to ${endDate}`
    );
  };

  const refreshList = () => {
    setError("");
    setPageData([]);

    if (startDate && endDate) {
      retrieveAuditTrailsByDateRange();
      return;
    }

    retrieveCurrentMonthAuditTrails();
  };

  const resetToCurrentMonth = () => {
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
    setError("");
    setPageData([]);
    retrieveCurrentMonthAuditTrails();
  };

  const openAuditTrail = (audit) => {
    setSelectedAudit(audit);
    setAuditTrailsModal(true);
  };

  const handleRowOpen = (event, audit) => {
    if (!audit || shouldIgnoreRowClick(event.target)) return;
    openAuditTrail(audit);
  };

  const closeModal = () => {
    setAuditTrailsModal(false);
    setSelectedAudit(null);
  };

  useEffect(() => {
    retrieveCurrentMonthAuditTrails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retrieve_audit_trails = useSelector(
    (state) => state.retrieve_audit_trails.retrieve_audit_trails
  );

  useEffect(() => {
    if (retrieve_audit_trails.status !== app_consts.IDLE) {
      if (retrieve_audit_trails.status === app_consts.LOADING) {
        setLoading(true);
        setError("");
      } else {
        setLoading(false);

        if (retrieve_audit_trails.status === app_consts.FAIL) {
          setError(retrieve_audit_trails.message);
          messageToast(retrieve_audit_trails.message || RETRIEVE_AUDIT_TRAILS_ERR_MSG, app_consts.FAIL);
        } else if (retrieve_audit_trails.status === app_consts.SUCCESS) {
          const normalizedData = (retrieve_audit_trails.data || []).map(normalizeAuditTrailRecord);
          setPageData(sortAuditTrails(normalizedData));
        }

        dispatch(reset_retrieve_audit_trails());
      }
    }
  }, [retrieve_audit_trails, dispatch]);

  const statusFilters = useMemo(() => {
    const statuses = Array.from(
      new Set(page_data.map((entry) => getAuditTrail(entry)?.status).filter(Boolean))
    );
    return ["All Statuses", ...statuses.map((status) => formatToSentenceCase(status))];
  }, [page_data]);

  const typeFilters = useMemo(() => {
    const types = Array.from(
      new Set(page_data.map((entry) => getAuditTrail(entry)?.audit_type).filter(Boolean))
    );
    return ["All Types", ...types.map((type) => formatToSentenceCase(type))];
  }, [page_data]);

  const current_page_data = useMemo(() => {
    let filteredData = [...page_data];

    if (selectedStatusFilter !== "All Statuses") {
      filteredData = filteredData.filter(
        (entry) =>
          formatToSentenceCase(getAuditTrail(entry)?.status || "") === selectedStatusFilter
      );
    }

    if (selectedTypeFilter !== "All Types") {
      filteredData = filteredData.filter(
        (entry) =>
          formatToSentenceCase(getAuditTrail(entry)?.audit_type || "") === selectedTypeFilter
      );
    }

    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase();

      filteredData = filteredData.filter((entry) => {
        const auditTrail = getAuditTrail(entry);
        const user = getAuditUser(entry);

        return [
          getAuditUserName(entry),
          user?.email_address,
          user?.phone_number,
          user?.unique_identification_number,
          auditTrail?.message,
          auditTrail?.status,
          auditTrail?.audit_type,
          auditTrail?.inserted_at
            ? moment(auditTrail.inserted_at).format("YYYY-MM-DD HH:mm:ss")
            : "",
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(lowerSearch));
      });
    }

    return filteredData;
  }, [page_data, searchTerm, selectedStatusFilter, selectedTypeFilter]);

  useEffect(() => {
    if (skipInitialPageResetRef.current) {
      skipInitialPageResetRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [searchTerm, selectedStatusFilter, selectedTypeFilter, setCurrentPage]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(current_page_data.length / itemPerPage));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [currentPage, current_page_data.length, itemPerPage, setCurrentPage]);

  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = current_page_data.slice(indexOfFirstItem, indexOfLastItem);

  const selectedAuditTrail = getAuditTrail(selectedAudit);
  const selectedAuditData = selectedAuditTrail?.data;

  return (
    <>
      <Head title="Audit Trails" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Audit Trails</BlockTitle>
              <BlockDes className="text-soft">
                <p>{!(loading || error) && `Audit Trails: ${current_page_data.length}`}</p>
                <p>Range: {activeRangeLabel} (Africa/Lusaka)</p>
              </BlockDes>
              {!loading && (
                <div className="mt-2">
                  <Button color="light" outline className="bg-white d-none d-sm-inline-flex" onClick={refreshList}>
                    <span>Refresh List</span>
                  </Button>
                </div>
              )}
            </BlockHeadContent>

            {!(loading || error) && (
              <BlockHeadContent>
                <div className="toggle-wrap nk-block-tools-toggle">
                  <a
                    href="#more"
                    className="btn btn-icon btn-trigger toggle-expand me-n1"
                    onClick={(ev) => {
                      ev.preventDefault();
                      updateSm(!sm);
                    }}
                  >
                    <Icon name="more-v"></Icon>
                  </a>

                  <div className="toggle-expand-content" style={{ display: sm ? "block" : "none" }}>
                    <ul className="nk-block-tools g-3">
                      <li>
                        <Button color="light" outline className="btn-white" onClick={resetToCurrentMonth}>
                          <span>Current Month</span>
                        </Button>
                      </li>
                    </ul>
                  </div>
                </div>
              </BlockHeadContent>
            )}
          </BlockBetween>
        </BlockHead>

        {loading ? (
          <AuthLoader loading_message="Loading Audit Trails..." />
        ) : error ? (
          <AuthError error_message={error} />
        ) : (
          <Block>
            <div className="card card-bordered mb-4 audit-trails-filter">
              <div className="card-inner">
                <div className="audit-trails-filter__bar">
                  <div className="audit-trails-filter__field">
                    <label className="form-label" htmlFor="audit-start-date">
                      Start Date
                    </label>
                    <div className="form-control-wrap">
                      <div className="form-icon form-icon-left">
                        <Icon name="calendar"></Icon>
                      </div>
                      <input
                        id="audit-start-date"
                        type="date"
                        className="form-control audit-trails-filter__input"
                        value={startDate}
                        onChange={(event) => setStartDate(event.target.value)}
                      />
                    </div>
                  </div>
                  <div className="audit-trails-filter__field">
                    <label className="form-label" htmlFor="audit-end-date">
                      End Date
                    </label>
                    <div className="form-control-wrap">
                      <div className="form-icon form-icon-left">
                        <Icon name="calendar"></Icon>
                      </div>
                      <input
                        id="audit-end-date"
                        type="date"
                        className="form-control audit-trails-filter__input"
                        value={endDate}
                        onChange={(event) => setEndDate(event.target.value)}
                      />
                    </div>
                  </div>
                  <div className="audit-trails-filter__actions">
                    <Button color="primary" className="btn-dim audit-trails-filter__button" onClick={retrieveAuditTrailsByDateRange}>
                        Retrieve Date Range
                    </Button>
                    <Button
                      color="light"
                      outline
                      className="btn-white audit-trails-filter__button"
                      onClick={resetToCurrentMonth}
                    >
                        Current Month
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <ul className="nk-block-tools g-3 d-flex justify-end mb-3">
              <li>
                <div>
                  <div className="form-icon form-icon-right">
                    <Icon name="search"></Icon>
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search audit trails"
                  />
                </div>
              </li>

              <li className="preview-item">
                <UncontrolledDropdown>
                  <div className="btn-group">
                    <DropdownToggle color="light" outline className="dropdown-toggle-split btn-white">
                      <Icon name="chevron-down"></Icon>
                      <span>{selectedTypeFilter}</span>
                    </DropdownToggle>
                  </div>

                  <DropdownMenu className="mt-3">
                    <ul className="link-list-opt">
                      {typeFilters.map((type) => (
                        <li key={type}>
                          <DropdownItem
                            tag="a"
                            href="#audit-type"
                            onClick={(event) => {
                              event.preventDefault();
                              setSelectedTypeFilter(type);
                            }}
                          >
                            <span>{type}</span>
                          </DropdownItem>
                        </li>
                      ))}
                    </ul>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </li>

              <li className="preview-item">
                <UncontrolledDropdown>
                  <div className="btn-group">
                    <DropdownToggle color="light" outline className="dropdown-toggle-split btn-white">
                      <Icon name="chevron-down"></Icon>
                      <span>{selectedStatusFilter}</span>
                    </DropdownToggle>
                  </div>

                  <DropdownMenu className="mt-3">
                    <ul className="link-list-opt">
                      {statusFilters.map((status) => (
                        <li key={status}>
                          <DropdownItem
                            tag="a"
                            href="#audit-status"
                            onClick={(event) => {
                              event.preventDefault();
                              setSelectedStatusFilter(status);
                            }}
                          >
                            <span>{status}</span>
                          </DropdownItem>
                        </li>
                      ))}
                    </ul>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </li>
            </ul>

            {current_page_data.length > 0 ? (
              <div className="nk-tb-list is-separate is-medium mt-1 mb-3">
                <div className="card-inner p-0">
                  <DataTableBody>
                    <DataTableHead>
                      <DataTableRow size="sm">
                        <span>User</span>
                      </DataTableRow>
                      <DataTableRow>
                        <span>Message</span>
                      </DataTableRow>
                      <DataTableRow>
                        <span>Audit Type</span>
                      </DataTableRow>
                      <DataTableRow>
                        <span>Status</span>
                      </DataTableRow>
                      <DataTableRow>
                        <span>Timestamp</span>
                      </DataTableRow>
                      <DataTableRow className="text-end">
                        <span>Action</span>
                      </DataTableRow>
                    </DataTableHead>

                    {currentItems.map((item, index) => {
                      const auditTrail = getAuditTrail(item);
                      const user = getAuditUser(item);
                      const rowKey =
                        auditTrail?.id ||
                        `${user?.user_account_id || "audit"}-${auditTrail?.inserted_at || index}-${index}`;

                      return (
                        <DataTableItem
                          key={rowKey}
                          className="cursor-pointer"
                          role="button"
                          tabIndex={0}
                          onClick={(event) => handleRowOpen(event, item)}
                          onKeyDown={(event) => {
                            if (event.key !== "Enter" && event.key !== " ") return;
                            event.preventDefault();
                            openAuditTrail(item);
                          }}
                        >
                          <DataTableRow size="sm">
                            <span className="tb-product">
                              <span className="title">{getAuditUserName(item)}</span>
                            </span>
                            <div>
                              <span className="tb-sub">{user?.email_address || "--"}</span>
                            </div>
                          </DataTableRow>

                          <DataTableRow>
                            <span className="tb-sub">
                              {auditTrail?.message
                                ? auditTrail.message.length > 70
                                  ? `${auditTrail.message.substring(0, 70)}...`
                                  : auditTrail.message
                                : "--"}
                            </span>
                          </DataTableRow>

                          <DataTableRow>
                            <span className="tb-sub">
                              {auditTrail?.audit_type ? formatToSentenceCase(auditTrail.audit_type) : "--"}
                            </span>
                          </DataTableRow>

                          <DataTableRow>
                            <Badge color={getAuditStatusColor(auditTrail?.status)} className="badge-dot">
                              {auditTrail?.status ? auditTrail.status.toUpperCase() : "--"}
                            </Badge>
                          </DataTableRow>

                          <DataTableRow>
                            <span className="tb-sub">
                              {auditTrail?.inserted_at
                                ? moment(auditTrail.inserted_at).format("YYYY-MM-DD HH:mm:ss")
                                : "--"}
                            </span>
                          </DataTableRow>

                          <DataTableRow className="nk-tb-col-tools">
                            <div className="tb-odr-btns d-none d-sm-inline">
                              <Button color="primary" size="sm" className="btn btn-dim" onClick={() => openAuditTrail(item)}>
                                View
                              </Button>
                            </div>
                            <Button className="btn-pd-auto d-sm-none" onClick={() => openAuditTrail(item)}>
                              <Icon name="chevron-right"></Icon>
                            </Button>
                          </DataTableRow>
                        </DataTableItem>
                      );
                    })}
                  </DataTableBody>

                  {currentItems.length > 0 && (
                    <div className="mt-3">
                      <PaginationComponent
                        itemPerPage={itemPerPage}
                        totalItems={current_page_data.length}
                        paginate={paginate}
                        currentPage={currentPage}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <AuthNoData no_data_message="No Audit Trails Found." />
            )}
          </Block>
        )}
      </Content>

      <Modal isOpen={auditTrailsModal} toggle={closeModal} className="modal-dialog-centered" size="lg">
        <ModalHeader toggle={closeModal}>Audit Trail Details</ModalHeader>
        <ModalBody>
          <div className="row g-4">
            <LabelText text={getAuditUserName(selectedAudit)} label="User" />
            <LabelText text={selectedAuditTrail?.message || "--"} label="Message" />
            <LabelText
              text={selectedAuditTrail?.audit_type ? selectedAuditTrail.audit_type.toUpperCase() : "--"}
              label="Audit Type"
            />
            <LabelText
              text={selectedAuditTrail?.status ? selectedAuditTrail.status.toUpperCase() : "--"}
              label="Status"
              badgeStat
              badgeColor={getAuditStatusColor(selectedAuditTrail?.status)}
            />
            <LabelText
              text={
                selectedAuditTrail?.inserted_at
                  ? moment(selectedAuditTrail.inserted_at).format("YYYY-MM-DD HH:mm:ss")
                  : "--"
              }
              label="Timestamp"
            />
            <LabelText text={getAuditUser(selectedAudit)?.email_address || "--"} label="Email Address" />
            <LabelText text={getAuditUser(selectedAudit)?.phone_number || "--"} label="Phone Number" />
            <LabelText
              text={getAuditUser(selectedAudit)?.unique_identification_number || "--"}
              label="Unique Identification Number"
            />

            {selectedAuditData !== undefined && selectedAuditData !== null && (
              <>
                <div className="col-12 mt-4">
                  <BlockHead className="px-0 pb-0">
                    <BlockHeadContent>
                      <BlockTitle tag="h6">Audit Data</BlockTitle>
                    </BlockHeadContent>
                  </BlockHead>
                </div>

                {renderAuditDataRows(selectedAuditData)}
              </>
            )}
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default AuditTrailsList;
